import { FC, useEffect, useMemo, useState } from 'react';
import { DiagnosticPresentation } from './DiagnosticPresentation';
import { DiagnosticAssessment } from './DiagnosticAssessment';
import { DiagnosticSteps } from '../../models/DiagnosticStepsEnum';
import { DiagnosticFinished } from './DiagnosticFinished';
import { useAppSelector } from '../../redux/hooks';
import { useLrnSession } from '../../custom-hooks/useLrnSession';
import { selectUser } from '../../redux/reducers/userSlice';
import { ErrorBoundaryUI } from '../ErrorBoundary/ErrorBoundaryUI';

export enum DiagnosticStateEnum {
  NEW,
  SAVEANDQUIT,
  RESUME,
}
interface Props {
  setShowDiagnostic: (value: boolean) => void;
  activityId: string;
  latestCompletedDiagnostic: { id: string; isInferred: boolean; retakeAssigned: boolean } | null;
}

export const Diagnostic: FC<Props> = ({ setShowDiagnostic, activityId }) => {
  const [step, setStepDiagnostic] = useState<DiagnosticSteps>(0);
  const user = useAppSelector(selectUser);

  // The activityId is grade-based, so an incomplete diagnostic from a previous school
  // year would otherwise be offered for resume. Send the current school year's start date
  // (the most recent started-on across the student's sites) so the BFF can exclude older
  // sessions. Current school year = the latest school year that has already started.
  const schoolYearStartDate = useMemo(() => {
    const now = Date.now();
    const startedSchoolYears = (user?.sites ?? [])
      .flatMap(site => site?.siteSchoolYears ?? [])
      .map(schoolYear => schoolYear?.startOn)
      .filter((startOn): startOn is string => !!startOn && new Date(startOn).getTime() <= now);

    if (!startedSchoolYears.length) {
      return undefined;
    }

    return startedSchoolYears.reduce((latest, startOn) =>
      new Date(startOn).getTime() > new Date(latest).getTime() ? startOn : latest,
    );
  }, [user?.sites]);

  const sessionInfo = useLrnSession(activityId, user?.sub || '', false, schoolYearStartDate);
  const [diagnosticState, setDiagnosticState] = useState<DiagnosticStateEnum | null>(null);

  useEffect(() => {
    if (!sessionInfo.data) {
      return;
    }
    if (sessionInfo.data?.status === 'Incomplete') {
      setDiagnosticState(DiagnosticStateEnum.RESUME);
    } else {
      setDiagnosticState(DiagnosticStateEnum.NEW);
    }
  }, [sessionInfo.data]);

  // The student is usually scrolled down the presentation card when they press the start button, and
  // the assessment step reuses the same scrolling document — so without this the Learnosity player
  // opens mid-page. Keyed on the step transition rather than duplicated into each entry point's click
  // handler so that both "Let's get started!" and the resume card are covered, and so it lands after
  // React commits the swap: the presentation card unmounting and the fullscreen request re-resolving
  // `vh` both re-clamp the document scroll, and neither is finished while the click handler is still
  // running. Unlike requestFullscreen(), scrolling needs no user gesture, so running it later is safe.
  useEffect(() => {
    if (step === DiagnosticSteps.ASSESSMENT) {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [step]);

  //RETAKE LOGIC COMMENTED WAITING TBD

  //const [isRetakeModal, setIsRetakeModal] = useState(false);

  /*useEffect(() => {
    if (latestCompletedDiagnostic?.retakeAssigned && latestCompletedDiagnostic.isInferred) {
      setIsRetakeModal(true);
    }
  }, []);
  */

  if (!sessionInfo.data && sessionInfo.lookupFailed) return <ErrorBoundaryUI embedded />;

  return (
    <>
      {diagnosticState !== null && (
        <div
          data-testid="diagnostic-component"
          className={`tw-w-full tw-min-h-[100vh] tw-pb-[75px] tw-flex tw-justify-center paper-grid-bg ${
            step === DiagnosticSteps.ASSESSMENT ? '!tw-bg-indigo-50' : '!tw-bg-[#F9F8F5]'
          }`}
          id="diagnosticUI"
        >
          {step === DiagnosticSteps.DEMONSTRATE && (
            <div className="tw-mt-12" id="diagnosticUIStep1">
              <DiagnosticPresentation
                setDiagnosticState={setDiagnosticState}
                diagnosticState={diagnosticState}
                setStepDiagnostic={setStepDiagnostic}
                refechSessionInfo={sessionInfo.refetchLrnSession}
              />
            </div>
          )}
          {step === DiagnosticSteps.ASSESSMENT && (
            <DiagnosticAssessment
              diagnosticStep={step}
              setDiagnosticState={setDiagnosticState}
              sessionInfo={sessionInfo.data}
              sessionRefreshing={sessionInfo.refreshing}
              sessionLookupFailed={sessionInfo.requiredLookupFailed}
              activityId={activityId}
              setStep={setStepDiagnostic}
            />
          )}
          {step === DiagnosticSteps.FINISHED && <DiagnosticFinished setShowDiagnostic={setShowDiagnostic} />}
          {/*
          <Dialog
            header="Try you diagnostic again"
            onHide={() => {
              setIsRetakeModal(false);
            }}
            visible={isRetakeModal}
            content={
              <div>
                <p>Your teacher has assigned you a diagnostic retake</p>
                <Button onClick={() => setIsRetakeModal(false)} className="block mx-auto" label="Let's Go" rounded />
              </div>
            }
          />
       */}
        </div>
      )}
    </>
  );
};
